import React, { useState, useEffect, useRef } from 'react';
import '../styles/ActivityModal.css';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useCurriculumAdapter } from '../hooks/useCurriculumAdapter';
import { getBlockATemplateByCurriculum } from '../../progress/reflections/blockARegistry';

// Minimal activity shape needed by this modal
type MaterialItem = string | { label: string; link: string };

type InstructionItem =
  | string
  | {
      text: string;
      /**
       * Optional expandable details for this step.
       * Use for things like: learning objectives, safety prompt text, example prompts, etc.
       */
      details?: string[];
      type?: 'step';
    }
  | {
      /**
       * Render a titled, indented instruction section (like a handout).
       * Useful for complex "setup / run / reflection" workflows.
       */
      type: 'section';
      title: string;
      items: string[];
    };

interface ActivityGoal {
  id: string;
  type?: string;
  text: string; // long description
  shortText?: string; // short label for display
}

interface ActivityLite {
  id: string;
  title: string;
  description?: string;
  image?: string;
  materials?: MaterialItem[];
  instructions?: InstructionItem[];
  duration?: string;
  difficulty?: string;
  link?: string;
  /**
   * Optional list of goals associated with this activity.
   * Prefer `goals`; `tasks` is kept only for backward compatibility.
   */
  goals?: ActivityGoal[];
  // Deprecated: use `goals` instead
  tasks?: ActivityGoal[];
}

interface ActivityModalProps {
  activity: ActivityLite;
  isOpen: boolean;
  onClose: () => void;
  onStart?: (activityLink?: string) => void;
  /**
   * Controls the modal footprint.
   * - default: current size (good for quick previews)
   * - large: near-full-page (better for running missions in-class)
   */
  size?: 'default' | 'large';
  /**
   * Optional override: show a primary action button even if the activity has no `link`,
   * and control its label/behavior (e.g. "Open Game").
   */
  primaryAction?: {
    label: string;
    onClick: () => void;
  };
  levelId: string;
  curriculumId?: string; // Phase 2: optional, defaults to music
  onSendReflections?: () => void;
  /**
   * Controls whether the primary Start/Link button is shown.
   * Defaults to true for existing callers.
   */
  showStartButton?: boolean;
}

const ActivityModal: React.FC<ActivityModalProps> = ({
  activity,
  isOpen,
  onClose,
  onStart,
  size = 'default',
  primaryAction,
  levelId,
  curriculumId,
  onSendReflections,
  showStartButton = true,
}) => {
  useCurriculumAdapter({ curriculumId }); // keep hook for future curriculum-specific behavior
  const [imageError, setImageError] = useState(false);
  const [resolvedLink, setResolvedLink] = useState<string | undefined>(undefined);
  const [isVideo, setIsVideo] = useState<boolean>(false);
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [expandedTaskIds, setExpandedTaskIds] = useState<string[]>([]);
  const [showAllMaterials, setShowAllMaterials] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Resolve link and detect video types
  useEffect(() => {
    let active = true;
    const run = async () => {
      if (!activity.link) {
        setResolvedLink(undefined);
        setIsVideo(false);
        setShowVideo(false);
        return;
      }
      try {
        let url = activity.link;
        if (activity.link.startsWith('storage://')) {
          const storagePath = activity.link.replace('storage://', '');
          url = await getDownloadURL(ref(getStorage(), storagePath));
        }
        if (!active) return;
        setResolvedLink(url);
        const lower = url.toLowerCase();
        const isVid = /(\.mp4|\.webm|\.mov|\.m4v)(\?|$)/.test(lower);
        setIsVideo(isVid);
        setShowVideo(isVid);
      } catch (e) {
        console.error('Failed resolving activity link', e);
        if (!active) return;
        setResolvedLink(activity.link);
        const lower = activity.link.toLowerCase();
        const isVid = /(\.mp4|\.webm|\.mov|\.m4v)(\?|$)/.test(lower);
        setIsVideo(isVid);
        setShowVideo(isVid);
      }
    };
    run();
    return () => { active = false; };
  }, [activity.link]);

  if (!isOpen) return null;
  const isLarge = size === 'large';

  const goals: ActivityGoal[] | undefined = Array.isArray((activity as any).goals)
    ? (activity as any).goals
    : Array.isArray((activity as any).tasks)
    ? (activity as any).tasks
    : undefined;

  const materials = Array.isArray(activity.materials) ? activity.materials : [];
  const hasExtraMaterials = materials.length > 2;
  const visibleMaterials = showAllMaterials ? materials : materials.slice(0, 2);

  const isReflectAndProgress =
    typeof activity.title === 'string' &&
    activity.title.toLowerCase().includes('reflect and progress');
  const showReflectAndProgressCta = isReflectAndProgress && !!onSendReflections;
  const isMusicReflection = activity.id === 'reflect-and-progress' &&
    (!curriculumId || curriculumId.toLowerCase().startsWith('music'));
  const reflectionTemplate = isMusicReflection
    ? getBlockATemplateByCurriculum(levelId, curriculumId)
    : null;

  const getGoalIcon = (goal: ActivityGoal) => {
    if ((goal.type || '').toLowerCase() === 'dailyachievement') {
      return '🏆';
    }
    return '✋';
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const toggleGoalExpanded = (goalId: string) => {
    setExpandedTaskIds(prev =>
      prev.includes(goalId) ? prev.filter(id => id !== goalId) : [...prev, goalId]
    );
  };

  // kept for future use
  // const openLink = () => {
  //   if (activity.link) {
  //     if (activity.link.startsWith('http')) {
  //       window.open(activity.link, '_blank');
  //     } else {
  //       window.location.href = activity.link;
  //     }
  //   }
  // };

  const handleStart = () => {
    // Safety guard: if no handler is provided, do nothing.
    if (!onStart) return;
    if (isVideo) {
      setShowVideo(true);
      // attempt autoplay
      requestAnimationFrame(() => {
        try { videoRef.current?.play().catch(() => {}); } catch {}
      });
      return;
    }
    if (resolvedLink) {
      onStart(resolvedLink);
    }
  };

  const instructions = Array.isArray(activity.instructions) ? activity.instructions : [];
  const hasSectionedInstructions = instructions.some(
    (i) => typeof i === 'object' && i !== null && (i as any).type === 'section'
  );

  return (
    <div
      className={`activity-modal-overlay ${isLarge ? 'activity-modal-overlay--large' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`activity-modal ${isLarge ? 'activity-modal--large' : ''}`}>
        <div className="activity-modal-header">
          <div className="activity-header-content">
            <h2>{activity.title}</h2>
            <button className="close-activity-modal" onClick={onClose} aria-label="Close modal">
              ×
            </button>
          </div>
          {activity.duration && (
            <div className="activity-meta">
              <span className="activity-duration">⏱️ {activity.duration}</span>
              {activity.difficulty && (
                <span className={`activity-difficulty ${activity.difficulty.toLowerCase()}`}>
                  {activity.difficulty}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="activity-modal-content">
          <div className="activity-image-section">
            {showVideo && isVideo && resolvedLink && (
              <div style={{ width: '100%', marginBottom: '1rem' }}>
                <video
                  ref={videoRef}
                  src={resolvedLink}
                  controls
                  playsInline
                  style={{ width: '100%', borderRadius: 8, background: '#000' }}
                />
              </div>
            )}
            {(!showVideo || !isVideo) && (
              !imageError ? (
                <img 
                  src={activity.image} 
                  alt={activity.title}
                  className="activity-image"
                  onError={handleImageError}
                />
              ) : (
                <div className="activity-image-placeholder">
                  <div className="placeholder-icon">🎵</div>
                  <span>Activity Image</span>
                </div>
              )
            )}
            {goals && goals.length > 0 && (
              <div className="goals-section">
                <h4>Goals to Complete</h4>
                <ul className="goals-list">
                  {goals.map(goal => {
                    const label = goal.shortText || goal.text;
                    const isExpanded = expandedTaskIds.includes(goal.id);
                    return (
                      <li key={goal.id} className={`goal-item ${isExpanded ? 'expanded' : ''}`}>
                        <button
                          type="button"
                          className="goal-header"
                          onClick={() => toggleGoalExpanded(goal.id)}
                        >
                          <span className="goal-icon">{getGoalIcon(goal)}</span>
                          <span className="goal-text">{label}</span>
                          <span className="goal-chevron">{isExpanded ? '▾' : '▸'}</span>
                        </button>
                        {isExpanded && goal.text && (
                          <div className="goal-long-text">
                            {goal.text}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div className="activity-details">
            {isMusicReflection && (
              <section className="activity-description">
                <h3>Reflection Questions</h3>
                {reflectionTemplate ? (
                  <ol>
                    {reflectionTemplate.questions.map(question => (
                      <li key={question.id} style={{ marginBottom: 16 }}>
                        <strong>{question.prompt}</strong>
                        {question.type === 'multiple_choice' && question.options && (
                          <ul>{question.options.map((option, index) => <li key={index}>{option}</li>)}</ul>
                        )}
                        {question.type === 'emoji_rating' && question.emojiRange && (
                          <div>{question.emojiRange.emojis.join(' ')}</div>
                        )}
                      </li>
                    ))}
                  </ol>
                ) : <p>No reflection questions are available for this level.</p>}
              </section>
            )}
            <div className="activity-description">
              <h3>About This Activity</h3>
              <p>{activity.description}</p>
            </div>

            {materials.length > 0 && (
              <div className="activity-materials">
                <h4>Materials Needed</h4>
                <ul className="materials-list">
                  {visibleMaterials.map((material: MaterialItem, index: number) => {
                    if (typeof material === 'string') {
                      // If the string itself looks like a URL, render it as a link with the URL as label
                      const looksLikeUrl = /^(https?:)\/\//i.test(material);
                      if (looksLikeUrl) {
                        return (
                          <li key={index}>
                            <a href={material} target="_blank" rel="noopener noreferrer">{material}</a>
                          </li>
                        );
                      }
                      return <li key={index}>{material}</li>;
                    }
                    return (
                      <li key={index}>
                        <a href={material.link} target="_blank" rel="noopener noreferrer">{material.label}</a>
                      </li>
                    );
                  })}
                </ul>
                {hasExtraMaterials && (
                  <button
                    type="button"
                    className="materials-toggle"
                    onClick={() => setShowAllMaterials(prev => !prev)}
                    aria-expanded={showAllMaterials}
                  >
                    {showAllMaterials ? 'Show less' : `+${materials.length - 2} more`}
                  </button>
                )}
              </div>
            )}

            <div className="activity-instructions">
              <h4>Step-by-Step Instructions</h4>
              {instructions.length > 0 ? (
                hasSectionedInstructions ? (
                  <div className="instructions-sections">
                    {instructions.map((instruction: InstructionItem, index: number) => {
                      if (typeof instruction === 'string') {
                        return (
                          <div key={index} className="instruction-intro-line">
                            {instruction}
                          </div>
                        );
                      }

                      if ((instruction as any).type === 'section') {
                        const section = instruction as Extract<InstructionItem, { type: 'section' }>;
                        return (
                          <section key={index} className="instruction-section">
                            <div className="instruction-section-title">{section.title}</div>
                            <ul className="instruction-section-items">
                              {section.items.map((line, i) => (
                                <li key={i} className="instruction-section-item">
                                  <span className="instruction-outline-line">{line}</span>
                                </li>
                              ))}
                            </ul>
                          </section>
                        );
                      }

                      // Fallback: render as a normal step if mixed content
                      const step = instruction as Extract<InstructionItem, { text: string }>;
                      const details = Array.isArray((step as any).details) ? (step as any).details : [];
                      return (
                        <div key={index} className="instruction-intro-line">
                          {step.text}
                          {details.length > 0 && (
                            <ul className="instruction-details-list">
                              {details.map((line: string, i: number) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <ol className="instructions-list">
                    {instructions.map((instruction: InstructionItem, index: number) => {
                      if (typeof instruction === 'string') {
                        return <li key={index}>{instruction}</li>;
                      }

                      // Section objects shouldn't appear here (guarded by hasSectionedInstructions),
                      // but render them safely if they do.
                      if ((instruction as any).type === 'section') {
                        const section = instruction as Extract<InstructionItem, { type: 'section' }>;
                        return (
                          <li key={index}>
                            <div style={{ fontWeight: 700, color: '#0f172a' }}>{section.title}</div>
                            <ul className="instruction-details-list">
                              {section.items.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </li>
                        );
                      }

                      const step = instruction as Extract<InstructionItem, { text: string }>;
                      const details = Array.isArray(step.details) ? step.details : [];
                      const hasDetails = details.length > 0;

                      return (
                        <li key={index}>
                          <div style={{ fontWeight: 600, color: '#334155' }}>
                            {step.text}
                          </div>
                          {hasDetails && (
                            <details className="instruction-details">
                              <summary className="instruction-summary">View details</summary>
                              <ul className="instruction-details-list">
                                {details.map((line, i) => (
                                  <li key={i}>{line}</li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )
              ) : (
                <div className="instructions-empty">No instructions provided.</div>
              )}
            </div>
          </div>
        </div>

        <div className="activity-modal-footer">
          {/* For Reflect & Progress missions: show "Open Reflect and Progress" in the primary button slot and hide "Start Activity". */}
          {showStartButton && showReflectAndProgressCta && !isMusicReflection && (
            <button className="activity-link-button" onClick={onSendReflections}>
              📝 Open Reflect and Progress
            </button>
          )}
          {showStartButton && !showReflectAndProgressCta && !isMusicReflection && primaryAction && (
            <button className="activity-link-button" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </button>
          )}
          {showStartButton && !showReflectAndProgressCta && !isMusicReflection && !primaryAction && activity.link && (
            <button className="activity-link-button" onClick={handleStart}>
              {activity.link.endsWith('.png') || activity.link.endsWith('.pdf')
                ? '📄 Download Certificate'
                : activity.link.startsWith('http')
                ? '🔗 Open External Link'
                : '🎮 Start Activity'}
            </button>
          )}
          <button className="activity-close-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export { ActivityModal };
